/**
 * ExtensionGate — the shared "this extension is switched off" wall for
 * extension-backed case views (OSINT, Mobile Forensics, …).
 *
 * The nav hides gated views from the same merged extension state the API
 * gates on, so this renders only for a stale tab or a direct URL. Lives under
 * `features/` because `useExtensionEnabled` value-imports react-query, which
 * the purity rule keeps out of `src/components/**`.
 */

import { Lock } from 'lucide-react'
import type { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { useExtensionEnabled } from '@/hooks/use-extensions'

interface ExtensionGateProps {
  extensionId: string
  titleKey: string
  bodyKey: string
  /**
   * Put the loading line and the wall in a `<main>` landmark, for a route with no layout of
   * its own around them. The children pass through as they are.
   */
  landmark?: boolean
  children: ReactNode
}

/** A centred `<main>` around what the gate shows in place of its children, when asked. */
const GateLandmark: FC<{ landmark: boolean; children: ReactNode }> = ({ landmark, children }) =>
  landmark ? <main className="flex w-full flex-col items-center">{children}</main> : children

export const ExtensionGate: FC<ExtensionGateProps> = ({
  extensionId,
  titleKey,
  bodyKey,
  landmark = false,
  children,
}) => {
  const { t } = useTranslation()
  const gate = useExtensionEnabled(extensionId)

  if (gate.isLoading) {
    return (
      <GateLandmark landmark={landmark}>
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      </GateLandmark>
    )
  }
  if (!gate.enabled) {
    return (
      <GateLandmark landmark={landmark}>
        <Card className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
          <Lock className="h-10 w-10 text-muted-foreground" />
          <div className="text-base font-medium text-foreground">{t(titleKey)}</div>
          <p className="max-w-md text-sm text-muted-foreground">{t(bodyKey)}</p>
        </Card>
      </GateLandmark>
    )
  }
  return <>{children}</>
}
